export type TrackingDataEntry = string | null | undefined | boolean | { [index: string]: TrackingDataEntry }

export interface EtData {
  /**
   * An unique ID that is different every time ET is initialized.
   * Only used for the p4etoken.
   */
  sessionId: string | undefined | null,
  /**
   * Persistent ID to identify recurring visitors. Saved as a Cookie by ET.
   */
  userId: string | undefined | null,
  /**
   * An unique ID that is different every time ET is initialized.
   * Used to identify the page visit.
   */
  visitId: string | undefined | null,
  /**
   * JWT Token containing the User ID and session ID.
   * Unused by ET.
   */
  p4etoken: string | undefined | null
}

export class Tracking {
  isGTMAlreadyLoaded = false
  etData = {
    sessionId: null,
    userId: null,
    visitId: null,
    p4etoken: null,
  }

  /**
   * Initializes the GoogleTagManager for the page by adding a script tag. Can be called multiple times.
   */
  initGoogleTagManager(gtmKey: string) {
    if (!this.isGTMAlreadyLoaded && gtmKey) {
      // load gtm
      window.dataLayer = window.dataLayer ?? [{visitor_type: 'user'}]
      window.dataLayer.push({'gtm.start': new Date().getTime(), 'event': 'gtm.js'})
      // control visitor_type
      if (['googlebot', 'bingbot', 'msnbot'].some((ua) => window.navigator.userAgent.includes(ua))) {
        window.dataLayer[0].visitor_type = 'bot'
      }
      const firstScript = document.getElementsByTagName('script')[0]
      const newScript = document.createElement('script')
      newScript.async = true
      newScript.src = 'https://www.googletagmanager.com/gtm.js?id=' + gtmKey
      firstScript.parentNode!.insertBefore(newScript, firstScript)
      this.isGTMAlreadyLoaded = true
    }
    return this.isGTMAlreadyLoaded
  }

  /**
   * Adds a datalayer for GoogleTagManager.
   */
  trackDatalayer(data: Record<string, TrackingDataEntry>) {
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push(data)
    }
  }

  /**
   * Loads ET for custom persistent tracking and pushes informations from it to GoogleTagManager.
   */
  async initET(productName: string, versionNumber: string | null, etUrl: string) {
    if (typeof productName === 'undefined') {
      console.error('Storybook', 'initET needs an productName')
      return false
    }
    if (!this.etData.userId && etUrl) {
      const etUid = getEtUid()
      const etUrlParam = (etUid === null) ? '' : `&etuid=${etUid}`
      const completeEtUrl = `${etUrl}?product=${productName}&version=${versionNumber}${etUrlParam}`
      this.trackDatalayer({
        pageUrl: getPageUrl(),
        referrer: getReferrer(),
        product: productName,
        version: versionNumber,
        etUrl: completeEtUrl,
      })
      await this.loadEtData(completeEtUrl)
    }
    return true
  }

  private async loadEtData(etUrl: string) {
    try {
      const response = await fetch(etUrl, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          page_url: getPageUrl(),
          referrer: getReferrer(),
        }),
      })

      if (!response.ok) {
        console.error('Tracking: et server', response)
        return
      }
      const body = await response.json()

      this.etData = {
        sessionId: body['et-session-id'],
        userId: body['et-user-id'],
        visitId: body['et-visit-id'],
        p4etoken: body.p4e_token,
      }
      this.trackDatalayer({ user_id: this.etData.userId })
    } catch (e) {
      console.error('Tracking: et load', e)
    }
  }
}
